import React from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ChevronDown, Eye } from '@lifeomic/chromicons';
import { MenuGroupHeading } from '@lifeomic/chroma-react/components/Menu/MenuGroupHeading';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { ViewMode } from './Attachments/InlineAttachment';

const messages = defineMessages({
  viewModeGroupHeader: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.viewModeGroupHeader',
    defaultMessage: 'View Modes',
  },
  viewMode: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.viewMode',
    defaultMessage: 'View Mode',
  },
  editing: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.editing',
    defaultMessage: 'Edit',
  },
  editingDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.editingDescription',
    defaultMessage: 'Editing mode',
  },
  compact: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.compact',
    defaultMessage: 'Compact',
  },
  compactDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.compactDescription',
    defaultMessage: 'Results appear inline and graphs are hidden',
  },
  expanded: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.expanded',
    defaultMessage: 'Expanded',
  },
  expandedDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.expandedDescription',
    defaultMessage: 'Results, graphs and documents appear inline',
  },
  presentationExpanded: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.presentation',
    defaultMessage: 'Present Expanded',
  },
  presentationExpandedDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.presentationDescription',
    defaultMessage: 'Present Full Screen with all documents expanded',
  },
  presentationCompact: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.presentationCompact',
    defaultMessage: 'Present',
  },
  presentationCompactDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.presentationCompactDescription',
    defaultMessage: 'Present Full Screen',
  },
  print: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.print',
    defaultMessage: 'Print',
  },
  printDescription: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.printDescription',
    defaultMessage:
      'Show in a view that is optimized for printing or saving to PDF',
  },
});

const viewModeMessages = {
  editing: {
    title: messages.editing,
    description: messages.editingDescription,
  },
  compact: {
    title: messages.compact,
    description: messages.compactDescription,
  },
  expanded: {
    title: messages.expanded,
    description: messages.expandedDescription,
  },
  'presentation-expanded': {
    title: messages.presentationExpanded,
    description: messages.presentationExpandedDescription,
  },
  'presentation-compact': {
    title: messages.presentationCompact,
    description: messages.presentationCompactDescription,
  },
  print: { title: messages.print, description: messages.printDescription },
} as const satisfies Record<
  ViewMode,
  { title: MessageDescriptor; description: MessageDescriptor }
>;

const useStyles = makeStyles(theme => ({
  viewModeMenu: {
    minWidth: 'unset',
    maxWidth: theme.pxToRem(200),
  },
}));

export const ViewModePicker = ({
  mode,
  onSelect,
}: {
  mode: ViewMode;
  onSelect: (newMode: ViewMode) => void;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const options: (ViewMode | 'divider')[] = [
    'compact',
    'expanded',
    'divider',
    'presentation-compact',
    'presentation-expanded',
  ];

  return (
    <Menu
      className={classes.viewModeMenu}
      placement="bottom-start"
      aria-label={formatMessage(messages.viewMode)}
      anchorElement={
        <Button
          variant="text"
          icon={Eye}
          trailingIcon={ChevronDown}
          aria-label={formatMessage(messages.viewMode)}
        >
          {formatMessage(viewModeMessages[mode].title)}
        </Button>
      }
      items={[
        <MenuGroupHeading key="viewModeGroupHeader">
          {formatMessage(messages.viewModeGroupHeader)}
        </MenuGroupHeading>,
        ...options.map(mode => {
          if (mode === 'divider') {
            return <Divider key="divider" />;
          }
          return (
            <MenuItem
              key={mode}
              value={mode}
              onClick={() => onSelect(mode)}
              text={formatMessage(viewModeMessages[mode].title)}
              secondaryText={formatMessage(viewModeMessages[mode].description)}
            />
          );
        }),
      ]}
    />
  );
};
